import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getCurrentEvent } from "core-integration/src/store/events/reducers";
import {
  getActiveEntryInLeague,
  getCode,
} from "core-integration/src/store/leagues/reducers";
import {
  fetchLeagueCode,
  setCode,
} from "core-integration/src/store/leagues/thunks";
import { ILeague } from "core-integration/src/store/leagues/types";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { Button } from "plos/src/components/buttons/Button";
import ButtonLink from "plos/src/components/links/ButtonLink";
import Link from "plos/src/components/links/Link";
import PageTitle from "plos/src/components/PageTitle";
import { useLocalizedNavigate } from "plos/src/hooks/useLocalizedNavigate";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ReportNameButton from "../../../ReportNameButton";
import {
  headerButtonsWrapper,
  pageTitleWrapper,
  reportButtonWrapper,
  standingsHeader,
  standingsHeadingLink,
} from "./standingsHeading.css";

interface StandingsHeadingProps {
  league: ILeague;
  leagueNumber: number;
  type: "classic" | "h2h";
}

const StandingsHeading = ({
  league,
  leagueNumber = 0,
  type,
}: StandingsHeadingProps) => {
  const { t } = useTranslation("leaguesAndCups");
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useLocalizedNavigate();

  const player = useSelector(getPlayerData);
  const code = useSelector((state: RootState) =>
    leagueNumber ? getCode(state, leagueNumber) : null,
  );

  const entryInLeague = useSelector((state: RootState) =>
    getActiveEntryInLeague(state, leagueNumber, type),
  );

  const isAdmin = entryInLeague?.league?.entry_can_admin;
  const isEntryInLeague = entryInLeague?.league;

  const now = useSelector(getCurrentEvent);
  const started = now && league.start_event <= now.id;
  const isAdobeLeague =
    league.league_type === "s" && league.name === "Adobe Express Badge League";

  useEffect(() => {
    if (!isEntryInLeague && league?.code_privacy === "a" && leagueNumber) {
      dispatch(fetchLeagueCode(leagueNumber));
    }
  }, [dispatch, league, isAdmin, isEntryInLeague, leagueNumber]);

  let showInviteBtn = false;
  let showJoinBtn = false;

  if (league && !league.closed) {
    if (isAdmin) {
      showInviteBtn = true;
    } else {
      if (league) {
        if (isEntryInLeague) {
          if (league.code_privacy === "a") {
            showInviteBtn = true;
          }
          if (league.code_privacy === "l") {
            showInviteBtn = true;
          }
        }

        if (!isEntryInLeague) {
          if (league.code_privacy === "a") {
            showJoinBtn = true;
          }
        }
      }
    }
  }

  return (
    <header className={standingsHeader}>
      {league && (
        <div className={pageTitleWrapper}>
          <PageTitle title={league.name} />
          {isAdobeLeague && !started && (
            <p className={standingsHeadingLink}>
              <Trans
                ns="leaguesAndCups"
                i18nKey="standingsHeading.adobeExpressBadgeLeagueStartsSoon"
              >
                The Adobe Express Badge League starts soon -{" "}
                <Link
                  href={`${import.meta.env.VITE_HF_BASE}/en/news/4591714/`}
                  aria-label="Find out more about the Adobe Express Badge League"
                >
                  Find Out More
                </Link>
              </Trans>
            </p>
          )}
        </div>
      )}
      <div className={headerButtonsWrapper}>
        {showInviteBtn && entryInLeague && (
          <ButtonLink
            to={`/leagues/${entryInLeague.league.id}/invite`}
            size="small"
          >
            {t(
              "standingsHeading.invitePeople",
              "Invite people to join this league",
            )}
          </ButtonLink>
        )}

        {code && showJoinBtn && (
          <Button
            size="small"
            onPress={() =>
              player && player.entry
                ? dispatch(setCode(code))
                : navigate(`/leagues/auto-join/${code}`)
            }
          >
            {t("standingsHeading.joinThisLeague", "Join this league")}
          </Button>
        )}
      </div>
      <div className={reportButtonWrapper.desktop}>
        <ReportNameButton league={league} />
      </div>
    </header>
  );
};

export default StandingsHeading;
